import { useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { apiPost, ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';

interface FieldErrors {
  current?: string;
  password?: string;
  confirm?: string;
  general?: string;
}

export function PasswordResetModal(): ReactElement {
  const { refresh } = useAuth();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    if (current.length === 0) errs.current = 'Required';
    if (password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (password !== confirm) errs.confirm = 'Passwords do not match';
    return errs;
  }

  async function onSubmit(ev: FormEvent<HTMLFormElement>): Promise<void> {
    ev.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await apiPost<{ ok: boolean }>('/api/auth/change-password', {
        oldPassword: current,
        newPassword: password,
      });
      await refresh();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Could not change password';
      setErrors({ general: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="password-reset-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: 'rgba(0,0,0,0.55)', color: 'var(--text-main)' }}
      data-testid="password-reset-modal"
    >
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="flex w-full max-w-md flex-col gap-4 rounded-2xl border p-6 shadow-lg"
        style={{
          background: 'var(--bg-surface)',
          borderColor: 'var(--border)',
        }}
      >
        <header className="flex flex-col items-center gap-2 text-center">
          <div className="text-4xl" aria-hidden="true">🔒</div>
          <h2
            id="password-reset-title"
            className="text-xl font-semibold"
          >
            Set New Password
          </h2>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            For your security, please set a new password before continuing.
          </p>
        </header>

        <label className="flex flex-col gap-1 text-sm">
          <span>Current or Temporary Password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.current ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.current}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span>New Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.password ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.password}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span>Confirm New Password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            className="min-h-[48px] rounded-md border px-3 text-base"
            style={{
              background: 'var(--bg-surface)',
              borderColor: 'var(--border)',
              color: 'var(--text-main)',
            }}
          />
          {errors.confirm ? (
            <span className="text-xs" style={{ color: '#b91c1c' }}>
              {errors.confirm}
            </span>
          ) : null}
        </label>

        {errors.general ? (
          <p className="text-sm" style={{ color: '#b91c1c' }}>
            {errors.general}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="min-h-[48px] rounded-md font-semibold disabled:opacity-60"
          style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}
        >
          {submitting ? 'Saving…' : 'Set New Password'}
        </button>
      </form>
    </div>
  );
}
